/**
 * Ticket List Component
 * @description 티켓 목록 표시 컴포넌트 - 필터링, 검색, 페이지네이션
 */

import { useState, useEffect } from 'react';
import { useNavigate } from '@shared/router';
import {
  Badge, Button, Card, Input, Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue
} from '@shared/ui/primitives';
import {
    Search,
    ChevronLeft,
    ChevronRight,
    Filter,
    SortAsc,
    SortDesc,
    Calendar,
    User,
    Clock,
    AlertCircle,
    CheckCircle,
    XCircle,
    Zap,
    RefreshCw,
} from 'lucide-react';
import { getTickets } from '@/services/ticketApi';
import type { Ticket, TicketListParams, TicketType, TicketStatus, Priority } from '@/types/ticket.types';

interface TicketListProps {
    tenantId: string;
    ticketType?: TicketType;
    defaultFilters?: Partial<TicketListParams>;
}

const statusLabels: Record<TicketStatus, string> = {
    draft: '임시저장',
    submitted: '제출됨',
    in_progress: '진행중',
    pending: '대기중',
    resolved: '해결됨',
    closed: '완료',
    rejected: '거부됨',
    cancelled: '취소됨',
};

const priorityLabels: Record<Priority, string> = {
    low: '낮음',
    medium: '보통',
    high: '높음',
    critical: '긴급',
};

const priorityColors: Record<Priority, string> = {
    low: 'bg-gray-100 text-gray-800 border-gray-200',
    medium: 'bg-blue-100 text-blue-800 border-blue-200',
    high: 'bg-orange-100 text-orange-800 border-orange-200',
    critical: 'bg-red-100 text-red-800 border-red-200',
};

const statusColors: Record<TicketStatus, string> = {
    draft: 'bg-gray-100 text-gray-800 border-gray-200',
    submitted: 'bg-blue-100 text-blue-800 border-blue-200',
    in_progress: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    pending: 'bg-orange-100 text-orange-800 border-orange-200',
    resolved: 'bg-green-100 text-green-800 border-green-200',
    closed: 'bg-gray-100 text-gray-800 border-gray-200',
    rejected: 'bg-red-100 text-red-800 border-red-200',
    cancelled: 'bg-gray-100 text-gray-800 border-gray-200',
};

const getStatusIcon = (status: TicketStatus) => {
    switch (status) {
        case 'in_progress':
            return <Clock className="w-4 h-4" />;
        case 'resolved':
            return <CheckCircle className="w-4 h-4" />;
        case 'closed':
            return <CheckCircle className="w-4 h-4" />;
        case 'rejected':
            return <XCircle className="w-4 h-4" />;
        case 'pending':
            return <AlertCircle className="w-4 h-4" />;
        default:
            return <Clock className="w-4 h-4" />;
    }
};

const getPriorityIcon = (priority: Priority) => {
    switch (priority) {
        case 'critical':
            return <Zap className="w-4 h-4" />;
        case 'high':
            return <AlertCircle className="w-4 h-4" />;
        default:
            return null;
    }
};

export const TicketList: React.FC<TicketListProps> = ({
    tenantId,
    ticketType,
    defaultFilters = {},
}) => {
    const navigate = useNavigate();

    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);

    // 필터
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<TicketStatus | 'all'>('all');
    const [priorityFilter, setPriorityFilter] = useState<Priority | 'all'>('all');
    const [sortBy, setSortBy] = useState<string>('createdAt');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    useEffect(() => {
        loadTickets();
    }, [tenantId, ticketType, page, statusFilter, priorityFilter, sortBy, sortOrder]);

    const loadTickets = async () => {
        try {
            setIsLoading(true);

            const params: any = {
                ...defaultFilters,
                page,
                size: 20,
                sortBy,
                sortOrder,
            };

            if (ticketType) {
                params.ticketType = ticketType;
            }

            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }

            if (priorityFilter !== 'all') {
                params.priority = priorityFilter;
            }

            if (searchQuery) {
                params.q = searchQuery;
            }

            console.log('Loading tickets with params:', { tenantId, params });
            const response = await getTickets(tenantId, params);
            console.log('Tickets response:', response);
            setTickets(response.tickets);
            setTotal(response.total);
        } catch (error) {
            console.error('Failed to load tickets:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = () => {
        setPage(1);
        loadTickets();
    };

    const handleRefresh = () => {
        loadTickets();
    };

    const handleSort = (field: string) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };

    const handleTicketClick = (ticketId: string) => {
        navigate({ to: `/it-service/tickets/${ticketId}` as any });
    };

    const totalPages = Math.ceil(total / 20);

    return (
        <div className="space-y-6">
            {/* Enhanced Filters */}
            <Card className="p-6 bg-gradient-to-r from-blue-50 to-white border-blue-100">
                <div className="flex flex-col lg:flex-row gap-4">
                    {/* Search */}
                    <div className="flex-1">
                        <label className="text-sm font-medium text-foreground mb-2 block">
                            <Search className="w-4 h-4 inline mr-1" />
                            검색
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                type="text"
                                placeholder="제목, 설명, 티켓 ID로 검색..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                className="pl-9 border-blue-200 focus:border-blue-400"
                            />
                        </div>
                    </div>

                    {/* Status Filter */}
                    <div className="w-48">
                        <label className="text-sm font-medium text-foreground mb-2 block">
                            <Filter className="w-4 h-4 inline mr-1" />
                            상태
                        </label>
                        <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as TicketStatus | 'all')}>
                            <SelectTrigger className="border-blue-200 focus:border-blue-400">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">전체</SelectItem>
                                <SelectItem value="in_progress">진행중</SelectItem>
                                <SelectItem value="pending">대기중</SelectItem>
                                <SelectItem value="resolved">해결됨</SelectItem>
                                <SelectItem value="closed">완료</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Priority Filter */}
                    <div className="w-48">
                        <label className="text-sm font-medium text-foreground mb-2 block">
                            우선순위
                        </label>
                        <Select value={priorityFilter} onValueChange={(value) => setPriorityFilter(value as Priority | 'all')}>
                            <SelectTrigger className="border-blue-200 focus:border-blue-400">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">전체</SelectItem>
                                <SelectItem value="critical">긴급</SelectItem>
                                <SelectItem value="high">높음</SelectItem>
                                <SelectItem value="medium">보통</SelectItem>
                                <SelectItem value="low">낮음</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 items-end">
                        <Button onClick={handleSearch} className="bg-blue-600 hover:bg-blue-700">
                            <Search className="w-4 h-4 mr-1" />
                            검색
                        </Button>
                        <Button onClick={handleRefresh} variant="outline" className="border-blue-200">
                            <RefreshCw className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Sort Controls */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground">
                        총 {total}개의 티켓
                    </span>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">정렬:</span>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSort('createdAt')}
                            className={`gap-1 ${sortBy === 'createdAt' ? 'bg-blue-100 text-blue-700' : ''}`}
                        >
                            생성일
                            {sortBy === 'createdAt' && (
                                sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />
                            )}
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSort('priority')}
                            className={`gap-1 ${sortBy === 'priority' ? 'bg-blue-100 text-blue-700' : ''}`}
                        >
                            우선순위
                            {sortBy === 'priority' && (
                                sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />
                            )}
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSort('status')}
                            className={`gap-1 ${sortBy === 'status' ? 'bg-blue-100 text-blue-700' : ''}`}
                        >
                            상태
                            {sortBy === 'status' && (
                                sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />
                            )}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Enhanced Ticket List */}
            <div className="space-y-3">
                {isLoading ? (
                    <Card className="p-8 text-center">
                        <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
                        <p className="text-muted-foreground">티켓을 불러오는 중...</p>
                    </Card>
                ) : tickets.length === 0 ? (
                    <Card className="p-12 text-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Search className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-foreground mb-2">티켓이 없습니다</h3>
                        <p className="text-muted-foreground">검색 조건을 변경하거나 새 티켓을 생성해보세요.</p>
                    </Card>
                ) : (
                    tickets.map((ticket) => (
                        <Card
                            key={ticket.ticketId}
                            className="p-6 cursor-pointer hover:shadow-lg transition-all duration-200 border-l-4 hover:border-l-blue-500 group"
                            onClick={() => handleTicketClick(ticket.ticketId)}
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-3">
                                        <span className="font-mono text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                            {ticket.ticketId}
                                        </span>
                                        <Badge className={`${priorityColors[ticket.priority]} gap-1`}>
                                            {getPriorityIcon(ticket.priority)}
                                            {priorityLabels[ticket.priority]}
                                        </Badge>
                                        <Badge variant="outline" className={`${statusColors[ticket.status]} gap-1`}>
                                            {getStatusIcon(ticket.status)}
                                            {statusLabels[ticket.status]}
                                        </Badge>
                                    </div>
                                    <h3 className="font-semibold text-foreground mb-2 group-hover:text-blue-600 transition-colors">
                                        {ticket.title}
                                    </h3>
                                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                                        {ticket.description}
                                    </p>
                                    <div className="flex items-center gap-6 text-xs text-muted-foreground">
                                        <div className="flex items-center gap-1">
                                            <User className="w-4 h-4" />
                                            <span>보고자: {typeof ticket.reporter === 'object' ? ticket.reporter?.displayName || ticket.reporter?.userId : (ticket.reporterName || ticket.reporter)}</span>
                                        </div>
                                        {(ticket.assignee || ticket.assigneeName) && (
                                            <div className="flex items-center gap-1">
                                                <User className="w-4 h-4" />
                                                <span>담당자: {typeof ticket.assignee === 'object' ? ticket.assignee?.displayName || ticket.assignee?.userId : (ticket.assigneeName || ticket.assignee)}</span>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-1">
                                            <Calendar className="w-4 h-4" />
                                            <span>생성일: {new Date(ticket.createdAt).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="ml-4 text-right">
                                    <div className="text-xs text-muted-foreground mb-1">
                                        최종 수정
                                    </div>
                                    <div className="text-sm font-medium text-foreground">
                                        {new Date(ticket.updatedAt).toLocaleDateString()}
                                    </div>
                                </div>
                            </div>
                        </Card>
                    ))
                )}
            </div>

            {/* Enhanced Pagination */}
            {totalPages > 1 && (
                <Card className="p-4">
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">
                            전체 {total}개 중 {(page - 1) * 20 + 1}-{Math.min(page * 20, total)}개 표시
                        </p>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(1)}
                                disabled={page === 1}
                            >
                                처음
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(page - 1)}
                                disabled={page === 1}
                            >
                                <ChevronLeft className="w-4 h-4" />
                                이전
                            </Button>
                            <div className="flex items-center gap-1">
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                                    return (
                                        <Button
                                            key={pageNum}
                                            variant={page === pageNum ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => setPage(pageNum)}
                                            className={page === pageNum ? "bg-blue-600 hover:bg-blue-700" : ""}
                                        >
                                            {pageNum}
                                        </Button>
                                    );
                                })}
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(page + 1)}
                                disabled={page === totalPages}
                            >
                                다음
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(totalPages)}
                                disabled={page === totalPages}
                            >
                                마지막
                            </Button>
                        </div>
                    </div>
                </Card>
            )}
        </div>
    );
};

TicketList.displayName = 'TicketList';
